import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Activity } from '../models/activity.entity'
import { ActivitiesService } from '../activities.service'
import { ActivitiesRepository } from '../activities.repository'
import { Context } from '../../contexts/context.model'
import { ContextsService } from '../../contexts'
import {
  Execution,
  ExecutionsRepository,
  ExecutionsService,
} from '../../executions'
import { defaultIfEmpty, firstValueFrom } from 'rxjs'

interface ActivityDetailState {
  activity?: Activity
  contexts?: Context[]
  executions?: Execution[]
  logs?: { _id: string; logs: string | undefined }[]
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const ACTIVITY_DETAIL_INITIAL_STATE: ActivityDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class ActivityDetailStore {
  constructor(
    private activitiesService: ActivitiesService,
    private contextsService: ContextsService,
    private executionsService: ExecutionsService,
    private executionsRepository: ExecutionsRepository,
    private activitiesRepository: ActivitiesRepository,
  ) {}

  private state = signal<ActivityDetailState>(ACTIVITY_DETAIL_INITIAL_STATE)

  // Selectors
  readonly activity = computed(() => this.state().activity)
  readonly contexts = computed(() => this.state().contexts)
  readonly executions = computed(() => this.state().executions)
  readonly logs = computed(() => this.state().logs)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(activityId?: string, loadExecutions?: boolean): Promise<void> {
    if (!activityId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.activitiesService.read$(activityId).subscribe({
        next: async (data) => {
          const contexts =
            loadExecutions === true ? undefined : await this.getContexts()
          const executions =
            loadExecutions !== true
              ? undefined
              : await this.getExecutions(data._id)
          this.setData(
            data,
            this.state().activity?._id
              ? this.state().activity?._id !== data._id
              : false,
            contexts,
            executions,
          )
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async save(activity: Activity): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.activitiesService.upsert$(activity).subscribe({
        next: (data) => {
          this.setData(data, false)
          this.activitiesRepository.notify({ code: 'ACTIVITY_UPDATED' })
          resolve(true)
        },
        error: (error) => {
          this.setError(error)
          resolve(false)
        },
      })
    })
  }

  async getContexts(): Promise<Context[] | undefined> {
    return new Promise((resolve) => {
      this.contextsService.list$().subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          this.setError(error)
          resolve(undefined)
        },
      })
    })
  }

  async getExecutions(activityId?: string): Promise<Execution[] | undefined> {
    if (!activityId) {
      return
    }

    return new Promise((resolve) => {
      this.executionsService.list$({ 'activity._id': activityId }).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          this.setError(error)
          resolve(undefined)
        },
      })
    })
  }

  async loadLogs(executionId: string) {
    this.setIsLoading(true)

    const logs = await firstValueFrom(
      this.executionsService
        .loadLogs$(executionId)
        .pipe(defaultIfEmpty(undefined)),
    )
    this.setLogs([...(this.logs() ?? []), { _id: executionId, logs }])
  }

  async deleteExecution(execution: Execution): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.executionsRepository
        .alert('EXECUTION_DELETION', execution)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.executionsService.delete$(execution._id).subscribe({
              next: () => {
                resolve(true)
                this.executionsRepository.notify({
                  code: 'EXECUTION_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(
    activity: Activity,
    isModified?: boolean,
    contexts?: Context[],
    executions?: Execution[],
  ) {
    this.state.set({
      ...this.state(),
      activity,
      isModified: isModified ?? this.state().isModified,
      contexts: contexts ?? this.state()?.contexts,
      executions: executions ?? this.state()?.executions,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setLogs(logs?: { _id: string; logs: string | undefined }[]) {
    this.state.set({
      ...this.state(),
      isLoading: false,
      logs: logs ?? this.state().logs,
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
